/**
 * Operational status helpers for Lead Ops OS (Sprint 8.8 Phase 1).
 * Pure functions — buckets/risk/value derived from existing Lead fields.
 * Mirrors frontend/src/features/orders/services/orderOperationalStatus.js.
 */

export const OPERATIONAL_BUCKETS = Object.freeze([
  "new",
  "ready_to_quote",
  "awaiting_customer",
  "awaiting_provider",
  "quote_sent",
  "booked",
  "needs_review",
  "pending_identity",
]);

const BUCKET_LABELS = Object.freeze({
  new: "New",
  ready_to_quote: "Ready to Quote",
  awaiting_customer: "Awaiting Customer",
  awaiting_provider: "Awaiting Provider",
  quote_sent: "Quote Sent",
  booked: "Booked",
  needs_review: "Needs Review",
  pending_identity: "Waiting for email confirm",
  rejected: "Rejected",
  unknown: "Unknown",
});

/**
 * @param {string} bucket
 * @returns {string}
 */
export function getLeadOperationalLabel(bucket) {
  return BUCKET_LABELS[bucket] || BUCKET_LABELS.unknown;
}

/**
 * Prefer automated provisional quote totals over legacy pricingSuggestion.
 * @param {Object} lead
 * @param {Object|null|undefined} [quote] linked quote (detail view)
 * @returns {number|null}
 */
export function getLeadQuoteValue(lead, quote = null) {
  const linked = quote || lead?.__linkedQuote || lead?.linkedQuote || null;
  const explTotal = Number(linked?.automatedPreparation?.pricingExplanation?.finalTotal);
  if (Number.isFinite(explTotal) && explTotal > 0) return explTotal;
  const totalCost = Number(linked?.totalCost);
  if (Number.isFinite(totalCost) && totalCost > 0) return totalCost;
  const estimated = Number(linked?.estimatedCost);
  if (Number.isFinite(estimated) && estimated > 0) return estimated;

  // Denormalized summary from automated provisional quote (lead list).
  const autoTotal = Number(lead?.automatedPricing?.totalCost);
  if (Number.isFinite(autoTotal) && autoTotal > 0) return autoTotal;

  const suggested = Number(lead?.pricingSuggestion?.recommendation?.suggestedAmount);
  if (Number.isFinite(suggested) && suggested > 0) return suggested;
  const calculated = Number(lead?.pricingSuggestion?.calculatedSuggestion?.amount);
  if (Number.isFinite(calculated) && calculated > 0) return calculated;
  return null;
}

/**
 * @param {number|null|undefined} amount
 * @returns {string}
 */
export function formatLeadQuoteValue(amount) {
  if (amount == null || !Number.isFinite(Number(amount))) return "—";
  const n = Math.round(Number(amount));
  return `R${n.toLocaleString("en-ZA")}`;
}

/**
 * @param {Object} lead
 * @returns {boolean}
 */
function hasSuggestedAmount(lead) {
  return getLeadQuoteValue(lead) != null;
}

/**
 * @param {Object} lead
 * @returns {boolean}
 */
function isQualifiedForQuoting(lead) {
  return Boolean(lead?.qualification?.completeness?.qualifiedForQuoting);
}

/**
 * @param {Object} lead
 * @param {Object|null|undefined} [quote]
 * @returns {boolean}
 */
function requiresManualReview(lead, quote = null) {
  const linked = quote || lead?.__linkedQuote || lead?.linkedQuote || null;
  if (linked?.automatedPreparation?.reviewState === "manual_review_required") {
    return true;
  }
  return Boolean(lead?.pricingSuggestion?.recommendation?.requiresManualReview);
}

/**
 * @param {Object} lead
 * @returns {string|null}
 */
function getConvertedQuoteId(lead) {
  return (
    lead?.tracking?.conversionData?.quoteId ||
    lead?.convertedQuoteId ||
    lead?.quoteId ||
    null
  );
}

/**
 * ADR-002 precedence.
 * @param {Object} lead
 * @returns {keyof typeof BUCKET_LABELS}
 */
export function getLeadOperationalBucket(lead) {
  if (!lead || typeof lead !== "object") return "unknown";

  const status = String(lead.status || "").toLowerCase();

  if (status === "rejected" || status === "deleted") return "rejected";

  if (status === "pending_identity") return "pending_identity";

  // Needs Review: explicit pricing manual-review flag (incompleteness → risk chips)
  if (requiresManualReview(lead) && status !== "converted") {
    return "needs_review";
  }

  if (status === "converted") return "booked";

  if (getConvertedQuoteId(lead) && status !== "converted") {
    return "quote_sent";
  }

  if (status === "qualified" || lead?.bidding?.status === "open") {
    return "awaiting_provider";
  }

  if (
    status === "accepted" ||
    (isQualifiedForQuoting(lead) && hasSuggestedAmount(lead))
  ) {
    return "ready_to_quote";
  }

  if (status === "pending") return "awaiting_customer";

  if (status === "new") return "new";

  return "unknown";
}

/**
 * @param {Object} lead
 * @returns {{ key: string, label: string }}
 */
/**
 * @param {Object} lead
 * @param {Object|null|undefined} [quote]
 * @returns {{ key: string, label: string }}
 */
export function getLeadEngine(lead, quote = null) {
  const linked = quote || lead?.__linkedQuote || lead?.linkedQuote || null;
  const product = String(
    linked?.automatedPreparation?.pricingExplanation?.pricingProduct ||
      lead?.automatedPricing?.pricingProduct ||
      ""
  );
  if (product === "interprovince_shared") {
    return { key: "shared_load", label: "Shared Load" };
  }
  if (product === "local_exclusive") {
    return { key: "local", label: "Local" };
  }
  if (product === "intra_province_exclusive") {
    return { key: "intra_province", label: "Intra Province" };
  }
  if (product === "interprovince_exclusive") {
    return { key: "shared_load", label: "Exclusive LD (manual)" };
  }

  if (lead?.publishedTripId) {
    return { key: "shared_load", label: "Shared Load" };
  }

  const category = String(lead?.qualification?.category || "").toLowerCase();
  if (category === "long_distance_share_load") {
    return { key: "shared_load", label: "Shared Load" };
  }
  if (category === "local") {
    return { key: "local", label: "Local" };
  }
  if (category === "mid_distance") {
    return { key: "intra_province", label: "Intra Province" };
  }
  if (category === "special_load") {
    return { key: "special", label: "Special" };
  }

  return { key: "unknown", label: "Unknown" };
}

/**
 * @param {Object} lead
 * @returns {string|null}
 */
/**
 * @param {Object} lead
 * @param {Object|null|undefined} [quote]
 * @returns {string|null}
 */
export function getLeadTruckRecommendation(lead, quote = null) {
  const linked = quote || lead?.__linkedQuote || lead?.linkedQuote || null;
  const expl = linked?.automatedPreparation?.pricingExplanation;
  const fromQuote =
    expl?.selectedTruckSize ||
    expl?.recommendedTruckSize ||
    lead?.automatedPricing?.selectedTruckSize ||
    lead?.automatedPricing?.recommendedTruckSize ||
    null;
  if (fromQuote) return String(fromQuote);

  // Legacy / advisory suggestion (also used when corridor quotes omit truck tier).
  const size =
    lead?.pricingSuggestion?.recommendedTruckSize?.size ||
    lead?.pricingSuggestion?.recommendation?.suggestedTruckSize ||
    lead?.pricingSuggestion?.calculatedSuggestion?.truckSize ||
    null;
  if (size == null || size === "") return null;
  return String(size);
}

/**
 * Customer-facing load band for suggested truck (Small Move, Medium Move…).
 * @param {Object} lead
 * @param {Object|null|undefined} [quote]
 * @returns {string|null}
 */
export function getLeadTruckLoadLabel(lead, quote = null) {
  const linked = quote || lead?.__linkedQuote || lead?.linkedQuote || null;
  const expl = linked?.automatedPreparation?.pricingExplanation;
  const fromExpl = expl?.customerLabel;
  if (fromExpl) return String(fromExpl);
  const fromAuto = lead?.automatedPricing?.customerLabel;
  if (fromAuto) return String(fromAuto);
  return null;
}

/**
 * @param {Object} lead
 * @returns {{ key: string, label: string }}
 */
export function getLeadProviderStatus(lead) {
  const biddingStatus = String(lead?.bidding?.status || "none").toLowerCase();
  if (biddingStatus === "open") {
    return { key: "marketplace_open", label: "Marketplace Open" };
  }
  if (biddingStatus === "closed") {
    return { key: "bidding_closed", label: "Bidding Closed" };
  }
  if (String(lead?.status || "").toLowerCase() === "qualified") {
    return { key: "awaiting_offers", label: "Awaiting Offers" };
  }
  return { key: "no_provider", label: "No Provider" };
}

/**
 * @param {Object} lead
 * @returns {Array<{ key: string, label: string }>}
 */
export function getLeadRiskFlags(lead) {
  const flags = [];

  if (requiresManualReview(lead)) {
    flags.push({ key: "manual_review", label: "Manual Review" });
  }

  const from = lead?.moving_from || lead?.from || lead?.pickupAddress?.formattedAddress;
  const to = lead?.moving_to || lead?.to || lead?.deliveryAddress?.formattedAddress;
  if (!from || !to) {
    flags.push({ key: "route_pending", label: "Route Pending" });
  }

  const missing = lead?.qualification?.completeness?.missingFields || [];
  const missingLower = missing.map((f) => String(f).toLowerCase());
  const items = lead?.qualification?.items || {};

  const packingCue =
    missingLower.some((f) => f.includes("pack") || f.includes("box")) ||
    items.needsBoxes === true ||
    lead?.requestedAddOns?.packingService?.requested === true;
  if (packingCue) {
    flags.push({ key: "packing_missing", label: "Packing Missing" });
  }

  const inventoryMissing = missingLower.some(
    (f) =>
      f.includes("item") ||
      f.includes("inventory") ||
      f.includes("volume") ||
      f.includes("photo")
  );
  if (inventoryMissing) {
    flags.push({ key: "inventory_unconfirmed", label: "Inventory Unconfirmed" });
  } else if (
    lead?.qualification?.completeness?.hasAllRequiredFields === false &&
    !flags.some((f) => f.key === "manual_review" || f.key === "route_pending")
  ) {
    flags.push({ key: "inventory_unconfirmed", label: "Inventory Unconfirmed" });
  }

  return flags;
}

/**
 * @param {Object[]} leads
 * @returns {Record<string, number>}
 */
export function computeLeadOperationalSummary(leads = []) {
  const summary = Object.fromEntries(OPERATIONAL_BUCKETS.map((b) => [b, 0]));
  summary.rejected = 0;
  summary.unknown = 0;
  summary.total = 0;

  for (const lead of leads) {
    const actual = lead?.primaryLead || lead;
    const bucket = getLeadOperationalBucket(actual);
    if (summary[bucket] == null) summary[bucket] = 0;
    summary[bucket] += 1;
    summary.total += 1;
  }

  return summary;
}
